import { z } from "zod";
import {
  actorSchema,
  auditEntryRecordSchema,
  idSchema,
  instantSchema,
  moneySchema,
  stageSchema,
  subjectRefSchema,
} from "@amber/contracts";

const reasonSchema = z.string().trim().min(1);
const countSchema = z.number().int().nonnegative();
const userActorSchema = z.strictObject({ kind: z.literal("user"), user_id: idSchema("usr") });
const processActorSchema = z.strictObject({ kind: z.literal("process"), name: z.string().min(1) });

export const COST_SERVICES = [
  "webshare",
  "assemblyai",
  "pyannoteai",
  "openrouter",
  "elevenlabs",
  "openai",
  "gemini",
  "twitterapi_io",
  "newsapi_ai",
  "youtube_data_api",
  "browser",
] as const;
export const costServiceSchema = z.enum(COST_SERVICES);
export type CostService = z.infer<typeof costServiceSchema>;

export const SYNC_COST_STATES = ["admitted", "admitted_from_reserve", "settled", "estimated", "released"] as const;
export const ASYNC_COST_STATES = [
  "admitted",
  "admitted_from_reserve",
  "intent_recorded",
  "submitted",
  "settled",
  "released",
  "unknown_outcome",
] as const;
export const COST_ENTRY_STATES = [
  "admitted",
  "admitted_from_reserve",
  "intent_recorded",
  "submitted",
  "settled",
  "estimated",
  "released",
  "unknown_outcome",
] as const;
export const costEntryStateSchema = z.enum(COST_ENTRY_STATES);

export const ADMISSION_REFUSAL_REASONS = [
  "budget",
  "reserve_exhausted",
  "daily_limit",
  "live_cap",
  "live_mode_off",
] as const;
export const admissionRefusalReasonSchema = z.enum(ADMISSION_REFUSAL_REASONS);

const costEntryBase = {
  id: idSchema("cst"),
  service: costServiceSchema,
  category_id: idSchema("bud"),
  estimate: moneySchema,
  actual: moneySchema.nullable(),
  usage: z.record(z.string().regex(/^[a-z][a-z_]*$/), z.number().nonnegative()).nullable(),
  idempotency_key: z.string().min(1),
  provider_job_id: z.string().min(1).nullable(),
  attempt_id: idSchema("att").nullable(),
  item_id: idSchema("itm").nullable().optional(),
  source_id: idSchema("src").nullable().optional(),
  platform: z.string().min(1).nullable().optional(),
  live: z.boolean(),
  admitted_at: instantSchema,
  settled_at: instantSchema.nullable(),
  expires_at: instantSchema,
  released_at: instantSchema.optional(),
  task_id: idSchema("tsk").optional(),
  note: z.string().trim().min(1).optional(),
};

export const costEntryRecordSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({ ...costEntryBase, kind: z.literal("sync"), state: z.enum(SYNC_COST_STATES) }),
    z.strictObject({
      ...costEntryBase,
      kind: z.literal("async"),
      state: z.enum(ASYNC_COST_STATES),
      intent_recorded_at: instantSchema.nullable(),
      submitted_at: instantSchema.nullable(),
    }),
  ])
  .refine((e) => (e.state === "settled") === (e.settled_at !== null), {
    message: "settled_at is set exactly when settled",
    path: ["settled_at"],
  })
  .refine((e) => e.state !== "released" || e.released_at !== undefined, {
    message: "a released entry records released_at",
    path: ["released_at"],
  })
  .refine((e) => e.state !== "unknown_outcome" || e.task_id !== undefined, {
    message: "an unknown outcome has an Operator task",
    path: ["task_id"],
  })
  .refine((e) => e.kind === "sync" || e.state !== "submitted" || e.provider_job_id !== null, {
    message: "a submitted job has a provider job ID",
    path: ["provider_job_id"],
  });
export type CostEntryRecord = z.infer<typeof costEntryRecordSchema>;

const shareSchema = z.number().min(0).max(1);
const thresholdSchema = z.number().positive();

export const budgetCategoryRecordSchema = z.strictObject({
  id: idSchema("bud"),
  name: z.string().trim().min(1),
  monthly_allowance: moneySchema,
  reserve_share: shareSchema,
  thresholds: z.strictObject({
    task: thresholdSchema,
    optional_suspended: thresholdSchema,
    essential_only: thresholdSchema,
  }),
  limits: z.strictObject({ daily: moneySchema, per_job: moneySchema, backfill: moneySchema }),
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  committed: moneySchema,
  reserve_used: moneySchema,
  revision: z.number().int().positive(),
});
export type BudgetCategoryRecord = z.infer<typeof budgetCategoryRecordSchema>;

export const TASK_TYPES = [
  "failed_items",
  "source_access",
  "coverage_gap",
  "route",
  "platform_issue",
  "suggested_sources",
  "draft_profile",
  "duplicate",
  "identity_change",
  "communication_match",
  "date_conflict",
  "carry_over",
  "correction_reconciliation",
  "reported_problem",
  "plan_review",
  "optional_review",
  "budget",
  "operational_failure",
  "drill",
] as const;
export const taskTypeSchema = z.enum(TASK_TYPES);

export const TASK_STATES = ["open", "done"] as const;
export const taskStateSchema = z.enum(TASK_STATES);

export const PRIORITY_LEVELS = ["high", "medium", "low"] as const;
export const priorityLevelSchema = z.enum(PRIORITY_LEVELS);

export const taskPrioritySchema = z
  .strictObject({
    type_weight: countSchema,
    age: countSchema,
    impact_people: countSchema,
    impact_items: countSchema,
    score: countSchema,
    level: priorityLevelSchema,
  })
  .refine((p) => p.score === p.type_weight + p.age + p.impact_people + p.impact_items, {
    message: "score is type weight + age + impact",
    path: ["score"],
  });

const taskBase = {
  id: idSchema("tsk"),
  type: taskTypeSchema,
  cause_key: z.string().min(1),
  subjects: z.array(subjectRefSchema).min(1),
  affected: z.strictObject({ items: countSchema, people: countSchema }),
  priority: taskPrioritySchema,
  opened_at: instantSchema,
};

export const taskRecordSchema = z.discriminatedUnion("state", [
  z.strictObject({ ...taskBase, state: z.literal("open"), closed: z.null() }),
  z.strictObject({
    ...taskBase,
    state: z.literal("done"),
    closed: z.discriminatedUnion("kind", [
      z.strictObject({
        kind: z.literal("closed_automatically"),
        at: instantSchema,
        by: processActorSchema,
        reason: reasonSchema,
        action: z.null(),
      }),
      z.strictObject({
        kind: z.literal("resolved_by_person"),
        at: instantSchema,
        by: userActorSchema,
        reason: reasonSchema,
        action: z.string().regex(/^[a-z][a-z_]*$/),
      }),
    ]),
  }),
]);
export type TaskRecord = z.infer<typeof taskRecordSchema>;

export const PAUSE_LEVELS = ["global", "stage", "source", "person"] as const;
export const pauseLevelSchema = z.enum(PAUSE_LEVELS);
export const PAUSE_STATES = ["active", "ended"] as const;
export const pauseStateSchema = z.enum(PAUSE_STATES);

const pauseEndBase = { at: instantSchema, by: actorSchema, reason: reasonSchema };
const resumeWithPreviewSchema = z.strictObject({
  ...pauseEndBase,
  cost_preview: z.strictObject({ waiting_items: countSchema, estimate: moneySchema }),
});
const resumeWithGapSchema = z.strictObject({ ...pauseEndBase, gap_id: idSchema("gap") });

const pauseBase = {
  id: idSchema("pau"),
  state: pauseStateSchema,
  reason: reasonSchema,
  started_by: actorSchema,
  started_at: instantSchema,
  start_audit_id: idSchema("aud"),
};

export const pauseRecordSchema = z
  .discriminatedUnion("level", [
    z.strictObject({
      ...pauseBase,
      level: z.literal("global"),
      scope: z.null(),
      ended: resumeWithPreviewSchema.nullable(),
    }),
    z.strictObject({
      ...pauseBase,
      level: z.literal("stage"),
      scope: z.strictObject({ stage: stageSchema }),
      ended: resumeWithPreviewSchema.nullable(),
    }),
    z.strictObject({
      ...pauseBase,
      level: z.literal("source"),
      scope: z.strictObject({ source_id: idSchema("src") }),
      ended: resumeWithGapSchema.nullable(),
    }),
    z.strictObject({
      ...pauseBase,
      level: z.literal("person"),
      scope: z.strictObject({ profile_id: idSchema("prf") }),
      ended: resumeWithGapSchema.nullable(),
    }),
  ])
  .refine((p) => (p.state === "ended") === (p.ended !== null), {
    message: "ended is set exactly when the pause has ended",
    path: ["ended"],
  });
export type PauseRecord = z.infer<typeof pauseRecordSchema>;

export const BULK_JOB_ACTIONS = ["retry", "reprocess"] as const;
export const bulkJobActionSchema = z.enum(BULK_JOB_ACTIONS);
export const BULK_JOB_STATES = ["previewed", "queued", "running", "completed", "cancelled"] as const;
export const bulkJobStateSchema = z.enum(BULK_JOB_STATES);

export const bulkJobRecordSchema = z
  .strictObject({
    id: idSchema("job"),
    action: bulkJobActionSchema,
    stage: stageSchema,
    cohort: z.strictObject({ item_ids: z.array(idSchema("itm")).min(1), filter: z.string() }),
    preview: z.strictObject({ items: countSchema, estimate: moneySchema, attempts_note: z.string().min(1) }),
    state: bulkJobStateSchema,
    progress: z.strictObject({ started: countSchema, finished: countSchema }),
    requested_by: actorSchema,
    confirmed_at: instantSchema.nullable(),
    completed_at: instantSchema.nullable(),
    cancelled: z
      .strictObject({ at: instantSchema, by: actorSchema, reason: reasonSchema, not_started: countSchema })
      .nullable(),
  })
  .refine((j) => j.progress.finished <= j.progress.started, {
    message: "finished cannot exceed started",
    path: ["progress"],
  })
  .refine((j) => (j.state === "previewed") === (j.confirmed_at === null), {
    message: "confirmed_at is set once confirmed",
    path: ["confirmed_at"],
  })
  .refine((j) => (j.state === "completed") === (j.completed_at !== null), {
    message: "completed_at is set exactly when completed",
    path: ["completed_at"],
  })
  .refine((j) => (j.state === "cancelled") === (j.cancelled !== null), {
    message: "cancelled is set exactly when cancelled",
    path: ["cancelled"],
  });
export type BulkJobRecord = z.infer<typeof bulkJobRecordSchema>;

export const ruleValueRevisionRecordSchema = z
  .strictObject({
    id: idSchema("rvs"),
    rule: z.string().regex(/^[a-z][a-z_]*(\.[a-z][a-z_]*)+$/),
    revision: z.number().int().positive(),
    base_revision: z.number().int().positive().nullable(),
    value: z.json(),
    actor: actorSchema,
    reason: reasonSchema,
    at: instantSchema,
    audit_id: idSchema("aud"),
  })
  .refine((r) => r.base_revision === (r.revision === 1 ? null : r.revision - 1), {
    message: "base_revision is the previous revision",
    path: ["base_revision"],
  });
export type RuleValueRevisionRecord = z.infer<typeof ruleValueRevisionRecordSchema>;

export const exampleRecordSchemas: Record<string, z.ZodType> = {
  "cost-entries.json": costEntryRecordSchema,
  "budget-categories.json": budgetCategoryRecordSchema,
  "tasks.json": taskRecordSchema,
  "pauses.json": pauseRecordSchema,
  "bulk-jobs.json": bulkJobRecordSchema,
  "rule-value-revisions.json": ruleValueRevisionRecordSchema,
  "audit-entries.json": auditEntryRecordSchema,
};
